import { Flex, HStack, Icon, Text } from '@chakra-ui/react';
import { createContext, useContext, useEffect, useRef } from 'react';
import type { ReactNode, Ref } from 'react';
import { createPortal } from 'react-dom';
import { TbChevronRight } from 'react-icons/tb';
import { PARTBAY_BLUE, SETTINGS_BORDER_COLOR } from './constants';

export interface SettingsBreadcrumb {
    label: string;
    /** Set on ancestors the user can return to; omitted on the current page. */
    onClick?: () => void;
}

export interface SettingsBreadcrumbBarProps {
    items: SettingsBreadcrumb[];
    /**
     * Ref to the right-aligned action slot. Settings.tsx hands the node to
     * SettingsBreadcrumbActionsProvider so nested screens can portal buttons
     * (a flow's Cancel/Next/Save) onto this row.
     */
    actionsRef?: Ref<HTMLDivElement>;
}

/** Height of the bar; the content area below it is sized against this. */
export const SETTINGS_BREADCRUMB_BAR_HEIGHT = '44px';

/**
 * Persistent top bar for the Settings takeover: tells the user where they are
 * and how to get back out. Rendered once by Settings.tsx above every screen so
 * the trail is in the same place on the overview, a section, and a detail page.
 */
const SettingsBreadcrumbBar = ({ items, actionsRef }: SettingsBreadcrumbBarProps) => (
    <Flex
        align="center"
        justify="space-between"
        gap={4}
        h={SETTINGS_BREADCRUMB_BAR_HEIGHT}
        px={4}
        flexShrink={0}
        borderBottomWidth="1px"
        borderColor={SETTINGS_BORDER_COLOR}
        bg="white"
    >
        <HStack spacing={1.5} minW={0}>
            {items.map((item, index) => {
                const isLast = index === items.length - 1;
                return (
                    <HStack key={`${item.label}-${index}`} spacing={1.5} minW={0}>
                        {index > 0 && (
                            <Icon
                                as={TbChevronRight as React.ElementType}
                                boxSize={3.5}
                                color="gray.400"
                                flexShrink={0}
                            />
                        )}
                        {item.onClick && !isLast ? (
                            <Text
                                as="button"
                                type="button"
                                fontSize="sm"
                                fontWeight="medium"
                                color="gray.500"
                                noOfLines={1}
                                _hover={{ color: PARTBAY_BLUE }}
                                onClick={item.onClick}
                            >
                                {item.label}
                            </Text>
                        ) : (
                            <Text
                                fontSize="sm"
                                fontWeight={isLast ? 'semibold' : 'medium'}
                                color={isLast ? 'gray.800' : 'gray.500'}
                                noOfLines={1}
                            >
                                {item.label}
                            </Text>
                        )}
                    </HStack>
                );
            })}
        </HStack>
        <HStack spacing={2} flexShrink={0} ref={actionsRef} />
    </Flex>
);

const SettingsDetailCrumbContext = createContext<
    ((items: SettingsBreadcrumb[] | undefined) => void) | undefined
>(undefined);

export const SettingsDetailCrumbProvider = ({
    onCrumbsChange,
    children,
}: {
    onCrumbsChange: (items: SettingsBreadcrumb[] | undefined) => void;
    children: ReactNode;
}) => (
    <SettingsDetailCrumbContext.Provider value={onCrumbsChange}>
        {children}
    </SettingsDetailCrumbContext.Provider>
);

/**
 * Appends crumbs for the page the caller renders, e.g. a user's name under
 * Users, or "Add users / Custom permissions" inside a flow. Any settings screen
 * nested inside a section calls this instead of drawing its own breadcrumb, so
 * every level of the area reads the same. The crumbs are removed when the page
 * unmounts.
 *
 * Callers pass a fresh array each render, so the effect keys off the labels
 * rather than the array identity; click handlers are read through a ref at
 * click time so they never go stale between label changes.
 */
export const useSettingsDetailCrumbs = (items: SettingsBreadcrumb[] | undefined) => {
    const setCrumbs = useContext(SettingsDetailCrumbContext);
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const labelKey = (items ?? []).map((item) => item.label).join(' ');

    useEffect(() => {
        const current = itemsRef.current;
        setCrumbs?.(
            current?.map((item, index) => ({
                label: item.label,
                onClick: item.onClick
                    ? () => itemsRef.current?.[index]?.onClick?.()
                    : undefined,
            })),
        );
        return () => setCrumbs?.(undefined);
    }, [labelKey, setCrumbs]);
};

/** Single-crumb form of useSettingsDetailCrumbs, for one nested page. */
export const useSettingsDetailCrumb = (label: string | undefined) => {
    useSettingsDetailCrumbs(label ? [{ label }] : undefined);
};

const SettingsBreadcrumbActionsContext = createContext<HTMLDivElement | null>(null);

export const SettingsBreadcrumbActionsProvider = ({
    container,
    children,
}: {
    container: HTMLDivElement | null;
    children: ReactNode;
}) => (
    <SettingsBreadcrumbActionsContext.Provider value={container}>
        {children}
    </SettingsBreadcrumbActionsContext.Provider>
);

/**
 * Portals its children onto the breadcrumb bar, right aligned. Used by screens
 * whose primary actions belong on the trail row rather than on a page title —
 * the multi-step flows, whose Cancel/Next/Save sit beside the breadcrumbs.
 */
export const SettingsBreadcrumbActions = ({ children }: { children: ReactNode }) => {
    const container = useContext(SettingsBreadcrumbActionsContext);
    return container ? createPortal(children, container) : null;
};

export default SettingsBreadcrumbBar;
